<template>
  <div>
    <!-- Toastification loading content export -->
    <toastification-loading-content ref="loadingToast" />

    <sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
      :unit-options="unitOptions"
    />

    <b-row v-if="showFilters" class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal
      @action="buildFilters()"
      @clear="clearSearchFilters()"
      v-if="showFilters"
    >
      <template v-slot:main-filters>
        <filters :unit-filter.sync="unitFilter" :unit-options="unitOptions" />
      </template>
    </filter-modal>

    <Transition name="slide-right-left">
      <div v-if="!showFilters && isFilterPanelOpen" class="filters-panel-fullscreen">
        <div class="filters-panel-header">
          <p class="h4 text-capitalize mb-2 text-white">
            {{ $t("messages.info.advanced_search") }}
          </p>
          <b-button
            variant="link"
            size="sm"
            @click="isFilterPanelOpen = false"
            class="text-dark p-0"
          >
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
        <div class="filters-panel-content">
          <filters :unit-filter.sync="unitFilter" :unit-options="unitOptions" />
        </div>
        <div class="filters-panel-footer">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="clearSearchFilters()"
            class="text-capitalize"
          >
            {{ $t("messages.info.clear_filters") }}
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="applyFilters()"
            class="text-capitalize"
          >
            {{ $t("search") }}
            <feather-icon icon="SearchIcon" />
          </b-button>
        </div>
      </div>
    </Transition>

    <!-- Table Container Card -->
    <b-card-group
      v-show="showFilters || !isFilterPanelOpen"
      deck
      :class="{ 'mx-2': showFilters, 'm-0': !showFilters }"
    >
      <Transition name="slide-left-right">
        <b-card class="m-0" no-body v-if="!isFormOpen">
          <div class="m-2">
            <b-row>
              <b-col
                cols="12"
                md="6"
                class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
                v-if="showFilters"
              >
                <label class="text-capitalize">{{ $t("table.show") }}</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                  data-cy="per-page-selector"
                />
                <label class="text-capitalize">{{ $t("table.entries") }}</label>
              </b-col>

              <!-- Search -->
              <b-col cols="12" :md="showFilters ? '6' : '12'">
                <div
                  class="d-flex align-items-center justify-content-end"
                  :class="{ 'w-100': !showFilters }"
                >
                  <b-form-input
                    data-cy="search"
                    v-model="searchQuery"
                    :class="
                      showFilters ? 'd-inline-block mr-1' : 'd-inline-block mr-1 w-75'
                    "
                    :placeholder="$t('table.search') + '...'"
                  />

                  <!-- Filter button for slot mode -->
                  <b-button-group v-if="!showFilters">
                    <b-button
                      @click="isFilterPanelOpen = true"
                      class="buttonFilter cursor-pointer d-flex align-items-center"
                      data-cy="filter-btn"
                    >
                      <b-icon-filter-circle-fill scale="1.1" />
                      <span class="align-middle ml-1 text-capitalize">{{
                        $t("filters")
                      }}</span>
                    </b-button>
                    <toggle-clear-filter
                      @clear-search-filters="clearSearchFilters"
                      :filters-count="filtersCount"
                    />
                  </b-button-group>

                  <b-button
                    variant="primary"
                    @click="openAdd()"
                    v-if="$can('create', 'UnitFloor') && showFilters"
                    :class="{ 'ml-1': !showFilters }"
                  >
                    <span class="text-nowrap text-capitalize">
                      {{ `${$t("actions.add")} ${$t("pisos.title")}` }}
                    </span>
                  </b-button>

                  <b-button
                    v-if="$can('import', 'UnitFloor') && showFilters"
                    v-b-modal.import_file
                    variant="info"
                    class="text-capitalize ml-1"
                    :class="{ 'px-2': !showFilters }"
                    type="button"
                    :disabled="buttonExport"
                  >
                    <feather-icon icon="UploadCloudIcon" size="15" />
                  </b-button>

                  <b-button
                    v-if="$can('export', 'UnitFloor')"
                    @click="exportExcel()"
                    variant="success"
                    class="text-capitalize ml-1"
                    :class="{ 'px-2': !showFilters }"
                    type="button"
                    :disabled="buttonExport"
                    v-b-tooltip.hover
                    :title="$t('messages.export.title')"
                  >
                    <feather-icon icon="DownloadCloudIcon" size="15" />
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </div>

          <b-table
            ref="refListTable"
            class="position-relative"
            :items="fetchList"
            responsive
            :fields="tableColumns"
            primary-key="id"
            :sort-by.sync="sortBy"
            show-empty
            :empty-text="$t('no_record_found')"
            :sort-desc.sync="isSortDirDesc"
          >
            <template #head()="data">
              <span> {{ $tc(data.label, 1) }}</span>
            </template>

            <template #head(name)="data">
              <span> {{ $tc(data.label, 2) }}</span>
            </template>

            <template #head(created_at)="data">
              <span> {{ $tc(data.label, 2) }}</span>
            </template>

            <template #head(actions)="data">
              <span> {{ $tc(data.label, 2) }}</span>
            </template>

            <template #cell(active)="data">
              <span v-if="data.item.active">
                {{ $t("pisos.active") }}
              </span>
              <span v-else>
                {{ $t("pisos.inactive") }}
              </span>
            </template>

            <template #cell(actions)="data">
              <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>

                <b-dropdown-item
                  @click="openEdit(data.item)"
                  v-if="$can('update', 'UnitFloor')"
                >
                  <feather-icon icon="EditIcon" />
                  <span class="align-middle ml-50 text-capitalize">{{
                    $t("actions.edit")
                  }}</span>
                </b-dropdown-item>

                <b-dropdown-item
                  @click="confirmDelete(data.item.id)"
                  v-if="$can('delete', 'UnitFloor')"
                >
                  <feather-icon icon="TrashIcon" />
                  <span class="align-middle ml-50 text-capitalize">{{
                    $t("actions.delete")
                  }}</span>
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted text-capitalize">{{
                  $t("table.showing", {
                    from: dataMeta.from,
                    to: dataMeta.to,
                    of: dataMeta.of,
                  })
                }}</span>
              </b-col>

              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <Pagination
                  :totalList="totalList"
                  :currentPage="currentPage"
                  @updatePagination="update"
                  :perPage="perPage"
                >
                </Pagination>
              </b-col>
            </b-row>
          </div>

          <!-- Modal Import -->
          <div>
            <b-modal
              id="import_file"
              :title="`${$t('messages.import.title')}`"
              no-close-on-esc
              no-close-on-backdrop
              ref="modalImport"
            >
              <vue-dropzone
                id="dropzone_file"
                ref="dropzoneFile"
                :options="dropzoneOptions"
                class="dropzone-file"
                @vdropzone-file-added="addFile"
                @vdropzone-removed-file="removeFile"
              />
              <div v-if="importMessage.length > 0" class="mt-2 scroll">
                <span v-for="(message, index) in importMessage" :key="index">
                  <p class="text-danger mb-0">
                    {{ message }}
                  </p>
                </span>
              </div>
              <template #modal-footer>
                <a :href="import_example_url" download>{{
                  $t("messages.import.example_file")
                }}</a>
                <b-button
                  variant="secondary"
                  @click="$bvModal.hide('import_file')"
                  :disabled="buttonExport"
                >
                  {{ $t("actions.cancel") }}
                </b-button>
                <b-button variant="primary" @click="importExcel" :disabled="buttonExport">
                  {{ $t("actions.accept") }}
                </b-button>
              </template>
            </b-modal>
          </div>
        </b-card>
      </Transition>
    </b-card-group>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import { ref, computed, getCurrentInstance } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import store from "@/store";
import vSelect from "vue-select";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import Pagination from "@/components/Pagination.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import ToggleClearFilter from "@/components/Filters/ToggleClearFilter.vue";
// import
import axios from "@/libs/axios";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationRemsLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";

export default {
  components: {
    Sidenav,
    Filters,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    BreadcrumbMenu,
    Pagination,
    FilterModal,
    ButtonFilters,
    ToggleClearFilter,
    //import
    vueDropzone: vue2Dropzone,
    ToastificationContent,
    ToastificationLoadingContent,
    ToastificationRemsLoadingContent,
  },
  props: {
    showFilters: {
      type: Boolean,
      default: true,
    },
  },
  setup(_, context) {
    const vm = getCurrentInstance().proxy;

    const isFilterPanelOpen = ref(false);
    const isFormOpen = ref(false);
    const { t } = useI18nUtils();
    const toast = useToast();
    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = item;
      isAdd.value = false;
    };

    const update = (data) => {
      currentPage.value = data;
    };

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      action,
      unitOptions,
      unitFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
    } = useList(_.showFilters);

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("pisos/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const exportExcel = () => {
      action.value = "export";
      fetchList();
    };

    const buttonExport = ref(false);

    // import

    const modalImport = ref();
    const importMessage = ref([]);
    const loadingToast = ref(null);
    const dropzoneFile = ref(null);
    const excelFile = ref(null);

    const addFile = (file) => {
      excelFile.value = file;
      importMessage.value = [];
      dropzoneFile.value = file;

      if (file.size > 10000000) {
        dropzoneFile.value = null;
        excelFile.value = null;
        alert("El archivo es muy grande, por favor, suba un archivo menor de 10MB");
      }
    };

    const trans = {
      "messages.import.upload": vm.$t("messages.import.upload"),
      "messages.import.uploading_file": vm.$t("messages.import.uploading_file"),
      "messages.import.upload_succesfully": vm.$t("messages.import.upload_succesfully"),
      "messages.export.download": vm.$t("messages.export.download"),
      "messages.export.Generating_file": vm.$t("messages.export.Generating_file"),
      "messages.error.server_error": vm.$t("messages.error.server_error"),
      "messages.delete.title": vm.$t("messages.delete.title"),
      "messages.delete.body": vm.$t("messages.delete.body"),
      "actions.delete": vm.$t("actions.delete"),
      "actions.cancel": vm.$t("actions.cancel"),
    };

    const removeFile = () => {
      dropzoneFile.value = null;
    };

    const importExcel = () => {
      if (dropzoneFile.value == null) return;
      if (excelFile.value == null) return;

      loadingToast.value.show(
        trans["messages.import.upload"],
        trans["messages.import.uploading_file"],
        "secondary"
      );

      buttonExport.value = true;
      importMessage.value = [];

      let formData = new FormData();
      formData.append("file", excelFile.value);
      formData.append("action", "import");

      axios
        .post("v1/unit-floors", formData)
        .then((response) => {
          if (response.data.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.data.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            excelFile.value = null;
            modalImport.value.hide();
            refetchData();
          }
        })
        .catch((error) => {
          const message = error?.response?.data?.message
            ? error.response.data.message
            : "Something went wrong";

          const rowErrors = error?.response?.data?.data ? error.response.data.data : [];

          importMessage.value = rowErrors;
          loadingToast.value.close();

          toast({
            component: ToastificationContent,
            props: {
              title: `${message}`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          buttonExport.value = false;
          loadingToast.value.close();
        });
    };

    const applyFilters = () => {
      buildFilters();
      isFilterPanelOpen.value = false;
    };

    return {
      update,
      buttonExport,
      exportExcel,
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      unitOptions,
      unitFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
      modalImport,
      loadingToast,
      removeFile,
      dropzoneOptions: {
        url: "https://httpbin.org/post",
        paramName: "file",
        autoProcessQueue: false,
        addRemoveLinks: true,
        dictRemoveFile: vm.$t("actions.delete"),
        acceptedFiles: ".xlsx,.xls",
        maxFilesize: 1,
        dictDefaultMessage: vm.$t("messages.import.select_file") + " (.xlsx .xls)",
      },
      dropzoneFile,
      importMessage,
      addFile,
      importExcel,
      import_example_url: `${window.__env.VUE_APP_BASE_URL}/import_examples/import_pisos.xlsx`,
      applyFilters,
      isFilterPanelOpen,
      isFormOpen,
    };
  },
};
</script>

